<template>
    <div
        v-if="isOpen"
        :id="id"
        class="drawer-overlay"
        :class="{ 'drawer-no-backdrop': !backdrop }"
        @click.self="onOverlayClick"
    >
        <transition name="drawer-slide" appear>
            <div :class="drawerPanelClass" role="document" @click.stop v-show="isOpen">
                <div class="drawer-content">
                    <div class="drawer-header" v-if="hasHeader">
                        <h5 class="drawer-title">
                            <slot name="title"></slot>
                        </h5>
                        <button
                            type="button"
                            class="drawer-close"
                            @click="close"
                            :aria-label="t('header-close')"
                        ><Icon icon-class="ui-x-close" size="4" /></button>
                    </div>
                    <div class="drawer-body">
                        <slot name="body" :close="close"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import Icon from "../../components/icon/icon";

export default {
    name: "drawer",
    components: { Icon },
    props: {
        id: String,
        hasHeader: {
            type: Boolean,
            default: true,
        },
        startsOpen: {
            type: Boolean,
            default: false,
        },
        backdrop: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: "",
            validator: function (value) {
                return ["", "sm", "lg", "xl"].indexOf(value) !== -1;
            }
        },
    },
    data() {
        return {
            isOpen: this.startsOpen,
        };
    },
    computed: {
        drawerPanelClass() {
            const base = "drawer-panel";
            return this.size ? `${base} drawer-${this.size}` : base;
        },
    },
    watch: {
        isOpen(val) {
            if (val) {
                if (this.backdrop) {
                    document.body.classList.add("drawer-open");
                }
                document.addEventListener("keydown", this.handleEscape);
            } else {
                document.body.classList.remove("drawer-open");
                document.removeEventListener("keydown", this.handleEscape);
            }
        },
    },
    mounted() {
        this.$root.$on("close-drawer", this.handleCloseEvent);
    },
    beforeDestroy() {
        this.forceCloseAndCleanup();
        this.$root.$off("close-drawer", this.handleCloseEvent);
    },
    methods: {
        open() {
            this.isOpen = true;
            this.$nextTick(() => this.$emit("show"));
        },
        close() {
            this.isOpen = false;
            this.$emit("hide");
        },
        onOverlayClick() {
            if (this.backdrop) {
                this.close();
            }
        },
        forceCloseAndCleanup() {
            this.isOpen = false;
            document.body.classList.remove("drawer-open");
            document.removeEventListener("keydown", this.handleEscape);
        },
        handleCloseEvent(drawerId) {
            if ((!drawerId || drawerId === this.id) && this.isOpen) {
                this.close();
            }
        },
        handleEscape(e) {
            if (e.key === "Escape" && this.isOpen) {
                this.close();
            }
        },
    },
};
</script>

<style scoped lang="less">
.drawer-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1040;
    display: flex;
    justify-content: flex-end;

    &.drawer-no-backdrop {
        background: transparent;
        pointer-events: none;

        .drawer-panel {
            pointer-events: auto;
        }
    }
}

.drawer-panel {
    width: 500px;
    max-width: 100vw;
    height: 100%;
    background: #fff;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    z-index: 1050;
    overflow: hidden;

    &.drawer-sm { width: 360px; }
    &.drawer-lg { width: 700px; }
    &.drawer-xl { width: 900px; }
}

.drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #E5E7EB;
    flex-shrink: 0;
}

.drawer-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.drawer-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background: #F3F4F6;
    }
}

.drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
    transition: transform 0.25s ease;
}

.drawer-slide-enter,
.drawer-slide-leave-to {
    transform: translateX(100%);
}
</style>

<style>
body.drawer-open {
    overflow: hidden;
}
</style>
